import { useState } from "react";
import { useExperimental } from "../context/ExperimentalFeaturesContext";

interface ExperimentalFeatureTogglesProps {
  /** Tighter layout for the sandbox bar popover. */
  compact?: boolean;
  onError?: (_msg: string) => void;
  onChanged?: (_key: string, _enabled: boolean) => void;
}

/**
 * The list of experimental-feature switches. Shared by the platform panel's
 * Test Environment tab and the in-app sandbox bar so both stay in sync.
 */
export default function ExperimentalFeatureToggles({ compact = false, onError, onChanged }: ExperimentalFeatureTogglesProps) {
  const { features, isEnabled, setFeature, saving, active } = useExperimental();
  const [localError, setLocalError] = useState<string | null>(null);

  if (features.length === 0) {
    return (
      <p className="exp-empty">
        No experimental features registered yet. Add one to <code>src/experimentalFeatures.ts</code> and it will appear here.
      </p>
    );
  }

  const toggle = async (key: string) => {
    const next = !isEnabled(key);
    setLocalError(null);
    try {
      await setFeature(key, next);
      onChanged?.(key, next);
    } catch (err) {
      const msg = (err as Error).message || "Couldn't update the feature.";
      setLocalError(msg);
      onError?.(msg);
    }
  };

  return (
    <div className={`exp-list ${compact ? "exp-list-compact" : ""}`}>
      {features.map((feature) => {
        const on = isEnabled(feature.key);
        const busy = saving === feature.key;
        const inputId = `exp-toggle-${feature.key}`;
        return (
          <div key={feature.key} className={`exp-row ${on ? "exp-row-on" : ""}`}>
            <div className="exp-row-text">
              <label htmlFor={inputId} className="exp-row-name">{feature.name}</label>
              {!compact && <div className="exp-row-desc">{feature.description}</div>}
              <div className="exp-row-meta">
                <span className="exp-row-area">{feature.area}</span>
                <code className="exp-row-key">{feature.key}</code>
              </div>
            </div>
            <span className="exp-switch">
              <input
                id={inputId}
                type="checkbox"
                role="switch"
                aria-checked={on}
                aria-label={`${feature.name}: ${on ? "on" : "off"}`}
                checked={on}
                disabled={!active || busy}
                onChange={() => toggle(feature.key)}
              />
              <span className="exp-switch-track" aria-hidden="true"><span className="exp-switch-thumb" /></span>
              <span className="exp-switch-state">{busy ? "…" : on ? "On" : "Off"}</span>
            </span>
          </div>
        );
      })}
      {localError && <div className="exp-error" role="alert">{localError}</div>}
    </div>
  );
}
